import { ArrowRight } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import Eyebrow from "../components/Eyebrow";
import CTAButton from "../components/CTAButton";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { TESTIMONIALS } from "../lib/site";

export default function FamilyExperiences() {
  const t = TESTIMONIALS.page;

  usePageMeta({
    title: "Family Experiences",
    description:
      "Experiences families have shared about working with Doris and CareCraft while looking for senior care in Los Angeles.",
    path: "/family-experiences",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "Family Experiences" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>Family experiences</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines lines={[<>Family</>, <>Experiences.</>]} />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">{t.intro}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite pb-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl space-y-10">
            {t.reviews.map((r, i) => (
              <Reveal key={r.name} delay={0.04 * i}>
                <article
                  data-testid={`experience-card-${i + 1}`}
                  className="rounded-2xl border border-inkline bg-ivory p-8 md:p-10"
                >
                  <blockquote className="font-serif text-xl leading-[1.7] text-charcoal/85 md:text-[1.35rem]">
                    {(r.text || ["Full review wording on file with CareCraft."]).map((p, j) => (
                      <p key={j} className={j > 0 ? "mt-4" : ""}>
                        {j === 0 ? "\u201C" : ""}
                        {p}
                        {j === (r.text || ["x"]).length - 1 ? "\u201D" : ""}
                      </p>
                    ))}
                  </blockquote>
                  <p className="mt-6 border-t border-inkline pt-4">
                    <span className="block font-medium text-forest">{r.name}</span>
                    {r.location && (
                      <span className="mt-0.5 block text-[0.85rem] text-charcoal/55">{r.location}</span>
                    )}
                    <span className="mt-0.5 block text-[0.8rem] text-charcoal/45">{r.meta}</span>
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-16 max-w-3xl">
              <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight text-forest md:text-4xl">
                Every family's situation is different.
              </h2>
              <p className="mt-5 text-lg leading-[1.7] text-charcoal/80">
                If you would like to understand what CareCraft's guidance could look like for your
                family, Doris would be glad to listen first.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <CTAButton to="/contact" testId="family-experiences-cta">
                  Talk With Doris
                </CTAButton>
                <a
                  href="tel:+18185901533"
                  data-testid="family-experiences-phone"
                  className="inline-flex min-h-[48px] items-center font-medium text-forest transition-colors hover:text-clay"
                >
                  Or call (818) 590-1533
                </a>
              </div>
              <p className="mt-6 flex items-center gap-2 text-[0.9rem] text-charcoal/60">
                <ArrowRight className="h-4 w-4 text-clay" aria-hidden="true" />
                CareCraft guidance is complimentary to families.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
